import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCheck, Pencil, Plus, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, PeriodInput, Loading, StatPill, NativeSelect, Confirm } from "@/components/common";
import { CustStatusBadge, ReconBadge, StageBadge } from "@/components/badges";
import { TopupDialog } from "@/components/TopupDialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { CUST_STATUS, currentPeriod, fmtDate, fmtDateTime, num, periodLabel, rupiah } from "@/lib/format";

const InfoBox = () => (
  <div className="border border-slate-300 bg-white rounded-sm px-4 py-3 text-xs text-slate-700 space-y-1" data-testid="recon-info">
    <div><b>Penjualan aktual</b> (voucher yang benar-benar terjual × harga) dipakai untuk menghitung kewajiban top up.</div>
    <div><b>Selisih</b> = Total top up aktual − Total uang penjualan aktual. Rekonsiliasi dihitung per periode. Rincian per pelanggan/jenis voucher hanya untuk informasi.</div>
    <div><b>Pasokan awal</b> pelanggan baru/aktif kembali tidak masuk kewajiban top up. Pembayarannya ditampilkan terpisah.</div>
  </div>
);

function StatusSummary({ by }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="recon-status-summary">
      {Object.keys(CUST_STATUS).map((k) => (
        <div key={k} className="border border-slate-300 bg-white rounded-sm px-3 py-2" data-testid={`recon-status-${k}`}>
          <CustStatusBadge status={k} />
          <div className="text-xs text-slate-600 mt-1">{num(by[k]?.customers || 0)} pelanggan · {num(by[k]?.qty || 0)} voucher</div>
          <div className="font-mono text-sm font-semibold">{rupiah(by[k]?.value || 0)}</div>
        </div>
      ))}
    </div>
  );
}

function TopupTable({ rows, canWrite, onEdit, onVoid }) {
  return (
    <div className="xl-wrap">
      <table className="xl" data-testid="topup-table">
        <thead><tr><th className="rownum">#</th><th>Tanggal</th><th>Jenis</th><th>Referensi</th><th>Pelanggan</th><th className="text-right">Nominal</th><th>Keterangan / Bukti</th><th>Status</th>{canWrite && <th>Aksi</th>}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={9} className="text-center text-slate-500 py-4">Belum ada top up tercatat</td></tr>}
          {rows.map((t, i) => (
            <tr key={t.id} className={t.status === "batal" ? "opacity-60 line-through" : ""} data-testid={`topup-row-${i}`}>
              <td className="rownum">{i + 1}</td><td>{fmtDate(t.date)}</td>
              <td className="text-xs">{t.kind === "penjualan" ? "Penjualan aktual" : "Pasokan awal (terpisah)"}</td>
              <td className="font-mono text-xs">{t.ref}</td><td className="text-xs">{t.customer_name || "-"}</td>
              <td className="num">{num(t.amount)}</td>
              <td className="text-xs max-w-[220px] truncate" title={`${t.note} ${t.proof}`}>{[t.note, t.proof, t.revisions?.length ? `dikoreksi ${t.revisions.length}x` : ""].filter(Boolean).join(" · ") || "-"}</td>
              <td className="text-xs">{t.status === "batal" ? `DIBATALKAN: ${t.void_reason}` : "AKTIF"}</td>
              {canWrite && <td className="whitespace-nowrap">{t.status === "aktif" && <>
                <Button size="sm" variant="ghost" onClick={() => onEdit(t)} data-testid={`topup-edit-${i}`}><Pencil className="h-4 w-4" /></Button>
                <VoidButton t={t} i={i} onVoid={onVoid} />
              </>}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VoidButton({ t, i, onVoid }) {
  const [reason, setReason] = useState("");
  return (
    <Confirm title={`Batalkan top up ${t.ref}?`} description={<span>Top up tidak dihapus, tetapi ditandai DIBATALKAN dan tercatat di audit log.<Textarea rows={2} className="mt-2" placeholder="Alasan (wajib, min. 5 karakter)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="topup-void-reason" /></span>}
      onConfirm={() => onVoid(t, reason)} testId={`topup-void-${i}`}
      trigger={<Button size="sm" variant="ghost" className="text-rose-600" data-testid={`topup-void-btn-${i}`}><Ban className="h-4 w-4" /></Button>} />
  );
}

function SalesTable({ rows, status }) {
  return (
    <div className="xl-wrap max-h-[55vh]">
      <table className="xl" data-testid="recon-table">
        <thead><tr><th className="rownum">No.</th><th>Nama Pelanggan</th><th>Status Pelanggan</th><th>Jenis Voucher</th><th className="text-right">Terjual Aktual</th><th className="text-right">Harga</th><th className="text-right">Total Uang Penjualan</th><th>Status Rekonsiliasi</th></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={8} className="text-center text-slate-500 py-4">Tidak ada penjualan aktual</td></tr>}
          {rows.map((r, i) => (
            <tr key={`${r.customer_id}-${r.voucher_type_id}`} data-testid={`recon-row-${i}`}>
              <td className="rownum">{i + 1}</td><td className="font-medium">{r.customer_name}</td><td><CustStatusBadge status={r.customer_status} /></td>
              <td>{r.voucher_name}</td><td className="num">{num(r.qty)}</td><td className="num">{num(r.price)}</td><td className="num">{num(r.value)}</td>
              <td><ReconBadge status={status} /></td>
            </tr>
          ))}
        </tbody>
        <tfoot><tr><td className="rownum">Σ</td><td colSpan={3}>TOTAL (terfilter)</td><td className="num">{num(rows.reduce((s, r) => s + r.qty, 0))}</td><td /><td className="num">{num(rows.reduce((s, r) => s + r.value, 0))}</td><td /></tr></tfoot>
      </table>
    </div>
  );
}

function Filters({ f, setF, types }) {
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  return (
    <div className="flex flex-wrap gap-2">
      <Input placeholder="Cari pelanggan..." value={f.q} onChange={set("q")} className="w-56 h-10" data-testid="recon-filter-customer" />
      <NativeSelect value={f.status} onChange={set("status")} data-testid="recon-filter-status">
        <option value="">Semua status</option>
        {Object.entries(CUST_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </NativeSelect>
      <NativeSelect value={f.type} onChange={set("type")} data-testid="recon-filter-voucher">
        <option value="">Semua jenis voucher</option>
        {types.map((t) => <option key={t} value={t}>{t}</option>)}
      </NativeSelect>
    </div>
  );
}

export default function Reconciliation() {
  const { can, tenant } = useAuth();
  const canWrite = can("topup:write");
  const [period, setPeriod] = useState(currentPeriod());
  const [rep, setRep] = useState(null);
  const [tops, setTops] = useState([]);
  const [custs, setCusts] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [note, setNote] = useState("");
  const [f, setF] = useState({ q: "", status: "", type: "" });
  const reqId = useRef(0);
  const load = useCallback(async () => {
    const id = ++reqId.current;
    try {
      const [r, t, c] = await Promise.all([api.get("/reconciliation", { params: { period } }), api.get("/topups", { params: { period } }), api.get("/customers")]);
      if (id !== reqId.current) return;
      setRep(r.data); setTops(t.data); setCusts(c.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [period]);
  useEffect(() => {
    setRep(null);
    load();
  }, [load]);
  const rows = useMemo(() => (rep ? rep.rows.filter((r) => (!f.status || r.customer_status === f.status) && (!f.type || r.voucher_name === f.type) && r.customer_name.toLowerCase().includes(f.q.toLowerCase())) : []), [rep, f]);
  const types = useMemo(() => [...new Set((rep?.rows || []).map((r) => r.voucher_name))], [rep]);
  const onVoid = async (t, reason) => {
    try { await api.post(`/topups/${t.id}/void`, { reason }); toast.success("Top up dibatalkan"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const confirm = async () => {
    try { setRep((await api.post("/reconciliation/confirm", { period, note })).data); setNote(""); toast.success("Rekonsiliasi dikonfirmasi & tercatat di audit log"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader sheet="SHEET 05" title="Rekonsiliasi Penjualan vs Top Up" subtitle={`${tenant?.name || ""} · Periode ${periodLabel(period)}`}>
        <PeriodInput value={period} onChange={setPeriod} />
        {canWrite && <Button onClick={() => setDlg({})} className="bg-emerald-700 hover:bg-emerald-800" data-testid="topup-add-btn"><Plus className="h-4 w-4 mr-2" />Catat Top Up</Button>}
      </PageHeader>
      {!rep ? <Loading /> : (
        <div className="space-y-4 fade-up">
          <InfoBox />
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span>Status periode:</span><StageBadge stage={rep.lock.stage} testId="recon-stage" />
            <span className="ml-3">Status rekonsiliasi:</span><ReconBadge status={rep.status} testId="recon-status" />
            {rep.confirm && <span className="text-xs text-slate-500">Dikonfirmasi {rep.confirm.by} · {fmtDateTime(rep.confirm.at)} (penjualan {rupiah(rep.confirm.value)}, top up {rupiah(rep.confirm.topup)})</span>}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatPill label="Voucher Terjual Aktual" value={num(rep.sales.qty)} tone="blue" testId="recon-stat-qty" />
            <StatPill label="Uang Penjualan Aktual" value={rupiah(rep.sales.value)} tone="blue" testId="recon-stat-value" />
            <StatPill label="Top Up Aktual" value={rupiah(rep.sales.topup)} tone="green" testId="recon-stat-topup" />
            <StatPill label="Selisih Top Up" value={rupiah(rep.sales.diff)} tone={rep.sales.diff < 0 ? "amber" : "slate"} testId="recon-stat-diff" />
            <StatPill label="Pembayaran Pasokan Awal (terpisah)" value={`${rupiah(rep.supply.initial_paid)} / ${rupiah(rep.supply.initial_value)}`} testId="recon-stat-initial" />
          </div>
          {canWrite && rep.status !== "belum" && (
            <div className="flex flex-wrap gap-2 items-center">
              <Input placeholder="Catatan rekonsiliasi (opsional)" value={note} onChange={(e) => setNote(e.target.value)} className="w-72 h-10" data-testid="recon-note-input" />
              <Confirm title="Konfirmasi rekonsiliasi periode ini?" description={`Penjualan ${rupiah(rep.sales.value)} vs top up ${rupiah(rep.sales.topup)} (selisih ${rupiah(rep.sales.diff)}) akan dicatat. Jika angka berubah setelahnya, status menjadi PERLU PEMERIKSAAN.`} onConfirm={confirm} testId="recon-confirm"
                trigger={<Button variant="outline" data-testid="recon-confirm-btn"><CheckCheck className="h-4 w-4 mr-2" />Rekonsiliasi</Button>} />
            </div>
          )}
          <h2 className="font-display font-semibold text-lg">Top Up Tercatat</h2>
          <TopupTable rows={tops} canWrite={canWrite} onEdit={(t) => setDlg(t)} onVoid={onVoid} />
          <h2 className="font-display font-semibold text-lg">Ringkasan per Status Pelanggan</h2>
          <StatusSummary by={rep.by_status} />
          <h2 className="font-display font-semibold text-lg">Rincian Penjualan Aktual (informasi)</h2>
          <Filters f={f} setF={setF} types={types} />
          <SalesTable rows={rows} status={rep.status} />
        </div>
      )}
      <TopupDialog target={dlg} period={period} customers={custs} onClose={() => setDlg(null)} onDone={() => { setDlg(null); load(); }} />
    </div>
  );
}
